/**
 * A Code 128 barcode, drawn as SVG — what every counter scanner reads.
 *
 * Code set B covers the letters, digits and punctuation tags and bar numbers
 * use. Drawn in the page rather than loaded from a library, so a tag prints
 * the same with no network and nothing to install.
 */

// Bar and space widths for each of the 107 Code 128 symbols (the stop has 7).
const PATTERNS = [
  '212222',
  '222122',
  '222221',
  '121223',
  '121322',
  '131222',
  '122213',
  '122312',
  '132212',
  '221213',
  '221312',
  '231212',
  '112232',
  '122132',
  '122231',
  '113222',
  '123122',
  '123221',
  '223211',
  '221132',
  '221231',
  '213212',
  '223112',
  '312131',
  '311222',
  '321122',
  '321221',
  '312212',
  '322112',
  '322211',
  '212123',
  '212321',
  '232121',
  '111323',
  '131123',
  '131321',
  '112313',
  '132113',
  '132311',
  '211313',
  '231113',
  '231311',
  '112133',
  '112331',
  '132131',
  '113123',
  '113321',
  '133121',
  '313121',
  '211331',
  '231131',
  '213113',
  '213311',
  '213131',
  '311123',
  '311321',
  '331121',
  '312113',
  '312311',
  '332111',
  '314111',
  '221411',
  '431111',
  '111224',
  '111422',
  '121124',
  '121421',
  '141122',
  '141221',
  '112214',
  '112412',
  '122114',
  '122411',
  '142112',
  '142211',
  '241211',
  '221114',
  '413111',
  '241112',
  '134111',
  '111242',
  '121142',
  '121241',
  '114212',
  '124112',
  '124211',
  '411212',
  '421112',
  '421211',
  '212141',
  '214121',
  '412121',
  '111143',
  '111341',
  '131141',
  '114113',
  '114311',
  '411113',
  '411311',
  '113141',
  '114131',
  '311141',
  '411131',
  '211412',
  '211214',
  '211232',
  '2331112',
];
const START_B = 104;
const STOP = 106;

/** The bar and space widths for a value, start to stop, with its check symbol. */
export function code128(value: string): number[] {
  const codes = [...value].map((ch) => {
    const c = ch.charCodeAt(0) - 32;
    // Outside code set B (accents, emoji): a question mark rather than a bad scan.
    return c >= 0 && c <= 94 ? c : 31;
  });
  const check = codes.reduce((sum, c, i) => sum + c * (i + 1), START_B) % 103;
  return [START_B, ...codes, check, STOP].flatMap((c) => [...PATTERNS[c]!].map(Number));
}

export function Barcode({ value, height = 36, module = 1.4 }: { value: string; height?: number; module?: number }) {
  const widths = code128(value);
  const quiet = 10;
  const total = widths.reduce((a, w) => a + w, 0) + quiet * 2;
  let x = quiet;
  const bars: { x: number; w: number }[] = [];
  widths.forEach((w, i) => {
    if (i % 2 === 0) bars.push({ x, w });
    x += w;
  });
  return (
    <svg
      role="img"
      aria-label={`Barcode ${value}`}
      viewBox={`0 0 ${total} ${height}`}
      width={total * module}
      height={height}
      preserveAspectRatio="none"
      shapeRendering="crispEdges"
    >
      <rect width={total} height={height} fill="#fff" />
      {bars.map((b, i) => (
        <rect key={i} x={b.x} y={0} width={b.w} height={height} fill="#000" />
      ))}
    </svg>
  );
}
